/**
 * drawingToolDefinitions.ts — Single source of truth for all drawing tools.
 *
 * Used by:
 *   - Desktop: DrawingToolRail, DrawingToolsPopover
 *   - Mobile: ToolsDrawer
 *   - Store: drawingStore (DrawingToolId)
 *   - Bridge: overlay-bridge (TOOL_TO_OVERLAY mapping)
 *
 * Groups: 'lines' | 'shapes' | 'fibonacci' | 'annotations' | 'tools'
 */

export type DrawingToolGroup = 'lines' | 'shapes' | 'fibonacci' | 'annotations' | 'tools';

export interface DrawingToolDefinition {
  id: string;
  label: string;
  icon: string;
  klineChartOverlayName: string;
  group: DrawingToolGroup;
}

export const DRAWING_TOOLS: readonly DrawingToolDefinition[] = [
  // ── Lines ──
  { id: 'trendLine',        label: 'Trend Line',    icon: 'TrendingUp',      klineChartOverlayName: 'segment',                 group: 'lines' },
  { id: 'horizontalLine',   label: 'Horizontal',    icon: 'Minus',           klineChartOverlayName: 'horizontalStraightLine',   group: 'lines' },
  { id: 'verticalLine',     label: 'Vertical',      icon: 'VerticalLine',   klineChartOverlayName: 'verticalStraightLine',     group: 'lines' },
  { id: 'ray',              label: 'Ray',           icon: 'MoveRight',       klineChartOverlayName: 'rayLine',                  group: 'lines' },
  { id: 'priceLine',        label: 'Price Line',    icon: 'PriceLine',       klineChartOverlayName: 'priceLine',                group: 'lines' },
  { id: 'parallelChannel',  label: 'Channel',       icon: 'ParallelChannel', klineChartOverlayName: 'parallelStraightLine',     group: 'lines' },

  // ── Shapes ──
  { id: 'rectangle',        label: 'Rectangle',     icon: 'Square',          klineChartOverlayName: 'rectangle',                group: 'shapes' },
  { id: 'circle',           label: 'Ellipse',       icon: 'CircleIcon',      klineChartOverlayName: 'ellipse',                  group: 'shapes' },

  // ── Fibonacci ──
  { id: 'fibonacci',        label: 'Fibonacci',     icon: 'Fibonacci',       klineChartOverlayName: 'customFibonacci',          group: 'fibonacci' },

  // ── Annotations ──
  { id: 'textAnnotation',   label: 'Text',          icon: 'TextAnnotation',  klineChartOverlayName: 'text',                     group: 'annotations' },
  { id: 'textTag',          label: 'Tag',           icon: 'Tag',             klineChartOverlayName: 'text',                     group: 'annotations' },
  { id: 'riskReward',       label: 'R:R',           icon: 'RiskReward',      klineChartOverlayName: 'riskReward',               group: 'annotations' },

  // ── Tools ──
  { id: 'measure',          label: 'Measure',       icon: 'Measure',         klineChartOverlayName: 'priceChannelLine',         group: 'tools' },
] as const;

/** Canonical set of all 12 tool IDs. */
export const DRAWING_TOOL_IDS = DRAWING_TOOLS.map((tool) => tool.id) as readonly string[];

/** Type-safe union of all drawing tool IDs. */
export type DrawingToolId = typeof DRAWING_TOOLS[number]['id'];

/** All groups in display order. */
export const DRAWING_TOOL_GROUPS: readonly DrawingToolGroup[] = [
  'lines', 'shapes', 'fibonacci', 'annotations', 'tools',
] as const;

/** Tool label lookup by ID. */
export const TOOL_LABELS: Record<string, string> = {};
for (const tool of DRAWING_TOOLS) {
  TOOL_LABELS[tool.id] = tool.label;
}

/** Overlay name lookup by tool ID (replaces overlay-bridge TOOL_TO_OVERLAY). */
export const TOOL_TO_OVERLAY: Record<string, string> = {};
for (const tool of DRAWING_TOOLS) {
  TOOL_TO_OVERLAY[tool.id] = tool.klineChartOverlayName;
}

/** Keyboard shortcut key for each tool ID (single letter, shown in tooltips). */
export const DRAWING_TOOL_SHORTCUT_KEYS: Record<string, string> = {
  trendLine: "T",
  horizontalLine: "H",
  verticalLine: "V",
  rectangle: "R",
  fibonacci: "F",
  textAnnotation: "N",
  textTag: "G",
  parallelChannel: "C",
  riskReward: "Q",
};
